import { useEffect, useState } from "react";
import type { RaceStateMessage } from "@cimara/protocol";
import type { RaceRegistration } from "../api/types";
import { formatCountdown, formatGap, formatStartAt } from "../race/format";

interface PenOverlayProps {
  race: RaceStateMessage;
  registration: RaceRegistration | null;
  raceName?: string;
}

/**
 * Pen and countdown screen. The server sends `race_state` at 2 Hz; the
 * countdown itself ticks locally so the last seconds stay smooth.
 */
export function PenOverlay({ race, registration, raceName }: PenOverlayProps) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, []);

  const countdown = formatCountdown(race.startAt - now);
  const counting = race.phase === "countdown";

  return (
    <div className="pen-overlay" data-testid="pen" role="dialog" aria-label="Race pen">
      <div className="pen-card">
        <p className="eyebrow">
          <span className="eyebrow__line" />
          {counting ? "Countdown" : "Pen"}
        </p>
        <h1 className="pen-title">{raceName ?? race.raceId ?? "Race"}</h1>
        <p className="pen-sub">
          {registration
            ? `Category ${registration.category} · Pen ${registration.pen}`
            : "Category self-selected"}
          {" · "}
          {race.total} {race.total === 1 ? "rider" : "riders"} ·{" "}
          {race.laps} {race.laps === 1 ? "lap" : "laps"}
        </p>

        <div className={`pen-countdown ${counting ? "pen-countdown--live" : ""}`}>
          <span className="pen-countdown__value">{countdown}</span>
          <span className="pen-countdown__label">
            {counting ? "to the start" : `Start ${formatStartAt(race.startAt)}`}
          </span>
        </div>

        <div className="pen-riders">
          <div className="pen-riders__head">
            <span>On the line</span>
            <span>Gap</span>
          </div>
          <ol className="pen-riders__list">
            {race.leaders.slice(0, 8).map((leader) => (
              <li key={`${leader.riderId}-${leader.position}`}>
                <span className="pen-riders__pos">{leader.position}</span>
                <span className="pen-riders__name">
                  {leader.name}
                  {leader.isBot ? <span className="live-board__bot">BOT</span> : null}
                </span>
                <span className="pen-riders__gap">{formatGap(leader.gap, leader.position)}</span>
              </li>
            ))}
            {race.leaders.length === 0 ? (
              <li className="pen-riders__empty">The field is rolling to the line…</li>
            ) : null}
          </ol>
        </div>

        <p className="pen-hint">
          Stay seated and keep pedalling. The start is automatic; the server owns the
          countdown.
        </p>
      </div>
    </div>
  );
}
